import { inject, Injectable } from '@angular/core';
import { NavigationError, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { NotificationsService } from './components/controls/notifications/notifications.service';
import { isStaleBuildError } from './util/stale-build-error';

/** Toast key for the "new version available" message. This is deliberately separate
 * from the global 'app-notification' toast, because only locally imported toasts
 * support an action button. */
export const staleBuildToastKey = 'app-update-required';

/** Detects when this tab is running a build that is no longer deployed.
 *
 * Lazy loaded modules are requested by hashed filename. Once a new version is
 * released those files no longer exist, so any navigation to a lazy route fails
 * and the app appears to do nothing when the user clicks. The tab can only recover
 * by loading the page again from the server.
 *
 * The user is prompted rather than reloaded automatically. Angular resolves
 * loadChildren before running canDeactivate guards, so an automatic reload would
 * discard unsaved work without the prompt those guards exist to provide.
 */
@Injectable({
	providedIn: 'root',
})
export class StaleBuildService {
	private router = inject(Router);
	private notificationsService = inject(NotificationsService);

	/** The route the user was trying to reach when the stale build was detected. */
	private failedUrl: string = null;

	/** Starts watching for lazy loaded modules failing to load. Called once from the
	 * root component, and the subscription then lives for the lifetime of the app. */
	monitor(): void {
		this.router.events
			.pipe(filter((event): event is NavigationError => event instanceof NavigationError))
			.subscribe((event) => {
				if (!isStaleBuildError(event.error)) return;

				this.failedUrl = event.url;

				this.notificationsService.addNotification({
					severity: 'warn',
					summary: 'A new version is available',
					detail: 'This page needs to be refreshed before you can continue. Save any changes first.',
					sticky: true,
					key: staleBuildToastKey,
				});
			});
	}

	/** Loads the page again from the server, which fetches the current build. Goes to
	 * wherever the user was originally headed so their original action completes. */
	reloadToLatestVersion(): void {
		window.location.href = this.failedUrl ?? window.location.href;
	}
}
